import {ColumnSpec} from '@mintdata-wsp/common/controllers/network/generated/requests/ColumnSpec';
import {TableSpec} from '@mintdata-wsp/common/controllers/network/generated/requests/TableSpec';
import {QueryParser} from '@mintdata-wsp/common/controllers/bases/QueryParser';
import {commonControllers} from '@mintdata-wsp/common/controllers/CommonControllers';
import {IFunctionContainer} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/ClientFunction';
import {TextUtil} from '@mintdata-wsp/common/util/TextUtil';
import {Function, Param} from '@mintdata/mint-kit/api/decorators/Function';
import {SheetType} from '@mintdata/mint-kit/api/functions/SheetAPI';
import {Type} from '@mintdata/mint-kit/api/types/Type';
import {IValue} from '@mintdata/mint-kit/api/types/Value';
import {ABoolean} from '@mintdata/mint-kit/api/values/ABoolean';
import {AError} from '@mintdata/mint-kit/api/values/AError';
import {AInteger} from '@mintdata/mint-kit/api/values/AInteger';
import {AString} from '@mintdata/mint-kit/api/values/AString';
import {IFrame} from '@mintdata/mint-kit/api/values/frames/Frame';
import {Frames} from '@mintdata/mint-kit/api/values/frames/Frames';
import {CoercionUtils} from '@mintdata/mint-kit/impl/coercionUtils/CoercionUtils';
import bind from 'bind-decorator';

export class TableFunctions implements IFunctionContainer
{
    private static NAME_REGEXP:RegExp = new RegExp('^[0-9a-zA-Z\-_]+$');

    public get namespace()
    {
        return 'table';
    }

    @Function({
        title: 'BASES()',
        tip: 'List all bases',
        alias: 'BASES',
        example: 'BASES()',
        icon: '',
        returns: {
            type: Type.Frame,
            title: 'Bases list',
            tip: 'Bases list'
        }
    })
    @bind
    public async bases():Promise<IFrame | AError>
    {
        return commonControllers.bases.baseList();
    }

    @Function({
        title: 'BASE_ADD(baseName)',
        tip: 'Create new base',
        alias: 'BASE_ADD',
        lazyOnly: true,
        example: 'BASE_ADD("main_base")',
        icon: '',
        returns: {
            type: Type.Boolean,
            title: 'Creation result',
            tip: 'Creation result'
        }
    })
    @bind
    public async baseAdd(
        @Param({
            title: 'baseName',
            tip: 'Base name',
            type: Type.String
        }) baseName:AString):Promise<ABoolean | AError>
    {
        if( baseName && TableFunctions.NAME_REGEXP.test(baseName.value) )
        {
            return commonControllers.bases.baseAdd(baseName.value);
        }

        return new AError(`#INVALID_BASE_NAME: ${baseName.value}`);
    }

    @Function({
        title: 'BASE_DELETE(baseName)',
        tip: 'Delete base',
        alias: 'BASE_DELETE',
        lazyOnly: true,
        example: 'BASE_DELETE("main_base")',
        icon: '',
        returns: {
            type: Type.Boolean,
            title: 'Deletion result',
            tip: 'Deletion result'
        }
    })
    @bind
    public async baseDelete(
        @Param({
            title: 'baseName',
            tip: 'Base name',
            type: Type.String
        }) baseName:AString):Promise<ABoolean | AError>
    {
        return commonControllers.bases.baseDelete(baseName.value);
    }

    @Function({
        title: 'BASE_RENAME(baseName, newName)',
        tip: 'Rename base',
        alias: 'BASE_RENAME',
        lazyOnly: true,
        example: 'BASE_RENAME("main_base", "users_base")',
        icon: '',
        returns: {
            type: Type.Boolean,
            title: 'Rename result',
            tip: 'Rename result'
        }
    })
    @bind
    public async baseRename(
        @Param({
            title: 'baseName',
            tip: 'Base name',
            type: Type.String
        }) baseName:AString,
        @Param({
            title: 'newName',
            tip: 'New name',
            type: Type.String
        }) newName:AString):Promise<ABoolean | AError>
    {
        return commonControllers.bases.baseRename(baseName.value, newName.value);
    }

    @Function({
        title: 'TABLES([baseName])',
        tip: 'List all tables',
        alias: 'TABLES',
        example: 'TABLES("main_base")',
        icon: '',
        returns: {
            type: Type.Frame,
            title: 'Tables list',
            tip: 'Tables list'
        }
    })
    @bind
    public async tableList(
        @Param({
            title: 'baseName',
            tip: 'Base name',
            type: Type.String,
            optional: true
        }) baseName:AString):Promise<IFrame | AError>
    {
        return commonControllers.bases.tableList(baseName && baseName.value);
    }

    @Function({
        title: 'TABLE_COLUMNS(baseName, tableName)',
        tip: 'Get information about table',
        alias: 'TABLE_COLUMNS',
        example: 'TABLE_COLUMNS("main_base", "users")',
        icon: '',
        returns: {
            type: Type.Frame,
            title: 'Table information',
            tip: 'Table information'
        }
    })
    @bind
    public async tableColumnsGet(
        @Param({
            title: 'baseName',
            tip: 'Base name',
            type: Type.String
        }) baseName:AString,
        @Param({
            title: 'tableName',
            tip: 'Table name',
            type: Type.String
        }) tableName:AString):Promise<IFrame | AError>
    {
        return commonControllers.bases.tableGet(baseName.value, tableName.value);
    }

    @Function({
        title: 'TABLE_SPEC(baseName, tableName, tableSpec)',
        tip: 'Get information about table',
        alias: 'TABLE_SPEC',
        lazyOnly: true,
        example: 'TABLE_SPEC("main_base", "users", {"id","number";"name","string";"desc","string";"assigned_to","string"})',
        icon: '',
        returns: {
            type: Type.Frame,
            title: 'Result table information',
            tip: 'Result table information'
        }
    })
    @bind
    public async tableSpec(
        @Param({
            title: 'baseName',
            tip: 'Base name',
            type: Type.String
        }) baseName:AString,
        @Param({
            title: 'tableName',
            tip: 'Table name',
            type: Type.String
        }) tableName:AString,
        @Param({
            title: 'tableSpec',
            tip: 'Table spec',
            type: Type.Frame
        }) specFrame:IFrame):Promise<IFrame | AError>
    {
        if( tableName && TableFunctions.NAME_REGEXP.test(tableName.value) )
        {
            const specOrError = this._parseFrameToSpec(specFrame);
            if( Type.isError(specOrError) )
                return specOrError as AError;

            const spec = specOrError as TableSpec;
            return commonControllers.bases.tableSpec(baseName.value, tableName.value, spec);
        }

        return new AError(`#INVALID_TABLE_NAME: ${tableName.value}`);
    }

    @Function({
        title: 'TABLE_SET_COLUMN_ORDER(baseName, tableName, orderFrame)',
        tip: 'Reorder table\'s columns',
        alias: 'TABLE_SET_COLUMN_ORDER',
        lazyOnly: true,
        example: 'TABLE_SET_COLUMN_ORDER("main_base", "users", {"id","name",desc"})',
        icon: '',
        returns: {
            type: Type.Frame,
            title: 'Result table information',
            tip: 'Result table information'
        }
    })
    @bind
    public async tableSetColumnOrder(
        @Param({
            title: 'baseName',
            tip: 'Base name',
            type: Type.String
        }) baseName:AString,
        @Param({
            title: 'tableName',
            tip: 'Table name',
            type: Type.String
        }) tableName:AString,
        @Param({
            title: 'orderFrame',
            tip: 'Order frame',
            type: Type.Frame
        }) orderFrame:IFrame):Promise<IFrame | AError>
    {
        const reordering = orderFrame.getFieldsUnsafe().map(f => f.name);
        const spec = new TableSpec(null, reordering, null, null);
        return commonControllers.bases.tableSpec(baseName.value, tableName.value, spec);
    }

    @Function({
        title: 'TABLE_DELETE_COLUMNS_EXCEPT(baseName, tableName, specFrameWithColumnsToPreserve)',
        tip: 'Remove extra table columns',
        alias: 'TABLE_DELETE_COLUMNS_EXCEPT',
        lazyOnly: true,
        example: 'TABLE_DELETE_COLUMNS_EXCEPT("main_base", "users", {"id","number";"name","string";"desc","string";"assigned_to","string"})',
        icon: '',
        returns: {
            type: Type.Frame,
            title: 'Result table information',
            tip: 'Result table information'
        }
    })
    @bind
    public async tableDeleteColumnsExcept(
        @Param({
            title: 'baseName',
            tip: 'Base name',
            type: Type.String
        }) baseName:AString,
        @Param({
            title: 'tableName',
            tip: 'Table name',
            type: Type.String
        }) tableName:AString,
        @Param({
            title: 'specFrameWithColumnsToPreserve',
            tip: 'Spec Frame with columns to preserve',
            type: Type.Frame
        }) specFrameWithColumnsToPreserve:IFrame):Promise<IFrame | AError>
    {
        const deleteAllExcept = specFrameWithColumnsToPreserve.getFieldsUnsafe().map(f => f.name);
        const spec = new TableSpec(null, null, null, deleteAllExcept);
        return commonControllers.bases.tableSpec(baseName.value, tableName.value, spec);
    }

    @Function({
        title: 'TABLE_DELETE_COLUMNS(baseName, tableName, args)',
        tip: 'Remove table columns',
        alias: 'TABLE_DELETE_COLUMNS',
        lazyOnly: true,
        example: 'TABLE_DELETE_COLUMNS("project_base", {"author","col2";"col3","col4"}, "col5")',
        icon: '',
        returns: {
            type: Type.Frame,
            title: 'Result table information',
            tip: 'Result table information'
        }
    })
    @bind
    public async tableDeleteColumns(
        @Param({
            title: 'baseName',
            tip: 'Base name',
            type: Type.String
        }) baseName:AString,
        @Param({
            title: 'tableName',
            tip: 'Table name',
            type: Type.String
        }) tableName:AString,
        @Param({
            title: 'args',
            tip: 'Arguments with column names to remove',
            type: SheetType.AnyIValue,
            repeatable: true
        }) args:IValue[]):Promise<IFrame | AError>
    {
        const deleteColumnsOrError = TextUtil.collectStringsFromIValues(args);
        if( Type.isError(deleteColumnsOrError) )
            return deleteColumnsOrError as AError;

        const deleteColumns = deleteColumnsOrError as Array<string>;
        const spec = new TableSpec(null, null, deleteColumns, null);
        return commonControllers.bases.tableSpec(baseName.value, tableName.value, spec);
    }

    @Function({
        title: 'TABLE_RENAME(baseName, tableName, newTableName)',
        tip: 'Rename table',
        alias: 'TABLE_RENAME',
        lazyOnly: true,
        example: 'TABLE_RENAME("main_base", "usrs_base", "users_base")',
        icon: '',
        returns: {
            type: Type.Boolean,
            title: 'Rename result',
            tip: 'Rename result'
        }
    })
    @bind
    public async tableRename(
        @Param({
            title: 'baseName',
            tip: 'Base name',
            type: Type.String
        }) baseName:AString,
        @Param({
            title: 'tableName',
            tip: 'Table name',
            type: Type.String
        }) tableName:AString,
        @Param({
            title: 'newTableName',
            tip: 'New table name',
            type: Type.String
        }) newTableName:AString):Promise<ABoolean | AError>
    {
        return commonControllers.bases.tableRename(baseName.value, tableName.value, newTableName.value);
    }

    @Function({
        title: 'TABLE_DELETE(baseName, tableName)',
        tip: 'Delete table',
        alias: 'TABLE_DELETE',
        lazyOnly: true,
        example: 'TABLE_DELETE("main_base", "users_base")',
        icon: '',
        returns: {
            type: Type.Boolean,
            title: 'Delete result',
            tip: 'Delete result'
        }
    })
    @bind
    public async tableDelete(
        @Param({
            title: 'baseName',
            tip: 'Base name',
            type: Type.String
        }) baseName:AString,
        @Param({
            title: 'tableName',
            tip: 'Table name',
            type: Type.String
        }) tableName:AString):Promise<ABoolean | AError>
    {
        return commonControllers.bases.tableDelete(baseName.value, tableName.value);
    }

    @Function({
        title: 'TABLE_ADD_ROWS(baseName, tableName, frameWithRowsToAdd)',
        tip: 'Insert new rows into the table',
        alias: 'TABLE_ADD_ROWS',
        lazyOnly: true,
        example: 'TABLE_ADD_ROWS("main_base", "users", A1)',
        icon: '',
        returns: {
            type: Type.Boolean,
            title: 'Add result',
            tip: 'Add result'
        }
    })
    @bind
    public async tableAddRows(
        @Param({
            title: 'baseName',
            tip: 'Base name',
            type: Type.String
        }) baseName:AString,
        @Param({
            title: 'tableName',
            tip: 'Table name',
            type: Type.String
        }) tableName:AString,
        @Param({
            title: 'frameWithRowsToAdd',
            tip: 'Frame with rows to add',
            type: Type.Frame
        }) frameWithRowsToAdd:IFrame):Promise<ABoolean | AError>
    {
        return commonControllers.bases.tableAddRows(baseName.value, tableName.value, frameWithRowsToAdd);
    }

    @Function({
        title: 'ADD_RECORDS(baseName, tableName, frameWithRowsToAdd)',
        tip: 'Insert new rows into the table',
        alias: 'ADD_RECORDS',
        lazyOnly: true,
        example: 'ADD_RECORDS("main_base", "users", A1)',
        icon: '',
        returns: {
            type: Type.Boolean,
            title: 'Add result',
            tip: 'Add result'
        }
    })
    @bind
    public async addRecords(
        @Param({
            title: 'baseName',
            tip: 'Base name',
            type: Type.String
        }) baseName:AString,
        @Param({
            title: 'tableName',
            tip: 'Table name',
            type: Type.String
        }) tableName:AString,
        @Param({
            title: 'frameWithRowsToAdd',
            tip: 'Frame with rows to add',
            type: Type.Frame
        }) frameWithRowsToAdd:IFrame):Promise<ABoolean | AError>
    {
        return commonControllers.bases.tableAddRows(baseName.value, tableName.value, frameWithRowsToAdd);
    }

    @Function({
        title: 'TABLE_UPSERT_ROWS(baseName, tableName, upsertColumnName, frameWithRowsToUpsert)',
        tip: 'Upsert rows into the table',
        alias: 'TABLE_UPSERT_ROWS',
        lazyOnly: true,
        example: 'TABLE_UPSERT_ROWS("projects_base", "task", "external_task_id", A1)',
        icon: '',
        returns: {
            type: Type.Boolean,
            title: 'Upsert result',
            tip: 'Upsert result'
        }
    })
    @bind
    public async tableUpsertRows(
        @Param({
            title: 'baseName',
            tip: 'Base name',
            type: Type.String
        }) baseName:AString,
        @Param({
            title: 'tableName',
            tip: 'Table name',
            type: Type.String
        }) tableName:AString,
        @Param({
            title: 'upsertColumnName',
            tip: 'Upsert column name',
            type: Type.String
        }) upsertColumnName:AString,
        @Param({
            title: 'frameWithRowsToUpsert',
            tip: 'Frame with rows to upsert',
            type: Type.Frame
        }) frameWithRowsToUpsert:IFrame):Promise<ABoolean | AError>
    {
        return commonControllers.bases.tableUpsertRows(baseName.value, tableName.value, upsertColumnName.value, frameWithRowsToUpsert);
    }

    @Function({
        title: 'UPSERT_RECORDS(baseName, tableName, upsertColumnName, frameWithRowsToUpsert)',
        tip: 'Upsert records into the table',
        alias: 'UPSERT_RECORDS',
        lazyOnly: true,
        example: 'UPSERT_RECORDS("projects_base", "task", "external_task_id", A1)',
        icon: '',
        returns: {
            type: Type.Boolean,
            title: 'Upsert result',
            tip: 'Upsert result'
        }
    })
    @bind
    public async tableUpsertRecords(
        @Param({
            title: 'baseName',
            tip: 'Base name',
            type: Type.String
        }) baseName:AString,
        @Param({
            title: 'tableName',
            tip: 'Table name',
            type: Type.String
        }) tableName:AString,
        @Param({
            title: 'upsertColumnName',
            tip: 'Upsert column name',
            type: Type.String
        }) upsertColumnName:AString,
        @Param({
            title: 'frameWithRowsToUpsert',
            tip: 'Frame with rows to upsert',
            type: Type.Frame
        }) frameWithRowsToUpsert:IFrame):Promise<ABoolean | AError>
    {
        return commonControllers.bases.tableUpsertRows(baseName.value, tableName.value, upsertColumnName.value, frameWithRowsToUpsert);
    }

    @Function({
        title: 'TABLE_ROWS(baseName, tableName, queryFrame)',
        tip: 'Query table rows',
        alias: 'TABLE_ROWS',
        example: 'TABLE_ROWS("main_base", "users", A1:C5)',
        icon: '',
        returns: {
            type: Type.Frame,
            title: 'Result rows',
            tip: 'Result rows'
        }
    })
    @bind
    public async tableRows(
        @Param({
            title: 'baseName',
            tip: 'Base name',
            type: Type.String
        }) baseName:AString,
        @Param({
            title: 'tableName',
            tip: 'Table name',
            type: Type.String
        }) tableName:AString,
        @Param({
            title: 'queryFrame',
            tip: 'Query frame',
            type: Type.Frame,
            optional: true
        }) queryFrame:IFrame):Promise<IFrame | AError>
    {
        try
        {
            const {query, settings} = new QueryParser(queryFrame || Frames.empty()).parse();
            return await commonControllers.bases.tableSelectRows(baseName.value, tableName.value, query, settings);
        }
        catch( e )
        {
            return new AError(e.message);
        }
    }

    @Function({
        title: 'GET_RECORDS(baseName, tableName, queryFrame)',
        tip: 'Query table records',
        alias: 'GET_RECORDS',
        example: 'GET_RECORDS("main_base", "users", A1:C5)',
        icon: '',
        returns: {
            type: Type.Frame,
            title: 'Result rows',
            tip: 'Result rows'
        }
    })
    @bind
    public async tableRecords(
        @Param({
            title: 'baseName',
            tip: 'Base name',
            type: Type.String
        }) baseName:AString,
        @Param({
            title: 'tableName',
            tip: 'Table name',
            type: Type.String
        }) tableName:AString,
        @Param({
            title: 'queryFrame',
            tip: 'Query frame',
            type: Type.Frame,
            optional: true
        }) queryFrame:IFrame):Promise<IFrame | AError>
    {
        try
        {
            const {query, settings} = new QueryParser(queryFrame || Frames.empty()).parse();
            return await commonControllers.bases.tableSelectRows(baseName.value, tableName.value, query, settings);
        }
        catch( e )
        {
            return new AError(e.message);
        }
    }

    @Function({
        title: 'TABLE_ROW_COUNT(baseName, tableName, queryFrame)',
        tip: 'Count table rows',
        alias: 'TABLE_ROW_COUNT',
        example: 'TABLE_ROW_COUNT("main_base", "users", A1:C5)',
        icon: '',
        returns: {
            type: Type.Integer,
            title: 'Result count',
            tip: 'Result count'
        }
    })
    @bind
    public async tableRowCount(
        @Param({
            title: 'baseName',
            tip: 'Base name',
            type: Type.String
        }) baseName:AString,
        @Param({
            title: 'tableName',
            tip: 'Table name',
            type: Type.String
        }) tableName:AString,
        @Param({
            title: 'queryFrame',
            tip: 'Query frame',
            type: Type.Frame
        }) queryFrame:IFrame):Promise<AInteger | AError>
    {
        try
        {
            const {query, settings} = new QueryParser(queryFrame).parse();
            return await commonControllers.bases.tableCountRows(baseName.value, tableName.value, query);
        }
        catch( e )
        {
            return new AError(e.message);
        }
    }

    @Function({
        title: 'COUNT_RECORDS(baseName, tableName, queryFrame)',
        tip: 'Count table records',
        alias: 'COUNT_RECORDS',
        example: 'COUNT_RECORDS("main_base", "users", A1:C5)',
        icon: '',
        returns: {
            type: Type.Integer,
            title: 'Result count',
            tip: 'Result count'
        }
    })
    @bind
    public async tableRecordsCount(
        @Param({
            title: 'baseName',
            tip: 'Base name',
            type: Type.String
        }) baseName:AString,
        @Param({
            title: 'tableName',
            tip: 'Table name',
            type: Type.String
        }) tableName:AString,
        @Param({
            title: 'queryFrame',
            tip: 'Query frame',
            type: Type.Frame
        }) queryFrame:IFrame):Promise<AInteger | AError>
    {
        try
        {
            const {query, settings} = new QueryParser(queryFrame).parse();
            return await commonControllers.bases.tableCountRows(baseName.value, tableName.value, query);
        }
        catch( e )
        {
            return new AError(e.message);
        }
    }

    @Function({
        title: 'TABLE_DELETE_ROWS_BY_ID(baseName, tableName, args)',
        tip: 'Delete rows by provided ids',
        alias: 'TABLE_DELETE_ROWS_BY_ID',
        lazyOnly: true,
        example: 'TABLE_DELETE_ROWS_BY_ID("projects_base", "task", {1,2; 3,4}, 5, 6 )',
        icon: '',
        returns: {
            type: Type.Boolean,
            title: 'Delete result',
            tip: 'Delete result'
        }
    })
    @bind
    public async tableDeleteRowsByIds(
        @Param({
            title: 'baseName',
            tip: 'Base name',
            type: Type.String
        }) baseName:AString,
        @Param({
            title: 'tableName',
            tip: 'Table name',
            type: Type.String
        }) tableName:AString,
        @Param({
            title: 'args',
            tip: 'Arguments with column ids to remove',
            type: SheetType.AnyIValue,
            repeatable: true
        }) args:IValue[]):Promise<ABoolean | AError>
    {
        const rowIdsOrError = TextUtil.collectIntegersFromIValues(args);
        if( Type.isError(rowIdsOrError) )
            return rowIdsOrError as AError;

        const rowIds = rowIdsOrError as Array<number>;
        return commonControllers.bases.deleteRowsByIds(baseName.value, tableName.value, rowIds);
    }

    @Function({
        title: 'DELETE_RECORDS_BY_ID(baseName, tableName, args)',
        tip: 'Delete rows by provided ids',
        alias: 'DELETE_RECORDS_BY_ID',
        lazyOnly: true,
        example: 'DELETE_RECORDS_BY_ID("projects_base", "task", {1,2; 3,4}, 5, 6 )',
        icon: '',
        returns: {
            type: Type.Boolean,
            title: 'Delete result',
            tip: 'Delete result'
        }
    })
    @bind
    public async deleteRecordsByIds(
        @Param({
            title: 'baseName',
            tip: 'Base name',
            type: Type.String
        }) baseName:AString,
        @Param({
            title: 'tableName',
            tip: 'Table name',
            type: Type.String
        }) tableName:AString,
        @Param({
            title: 'args',
            tip: 'Arguments with column ids to remove',
            type: SheetType.AnyIValue,
            repeatable: true
        }) args:IValue[]):Promise<ABoolean | AError>
    {
        const rowIdsOrError = TextUtil.collectIntegersFromIValues(args);
        if( Type.isError(rowIdsOrError) )
            return rowIdsOrError as AError;

        const rowIds = rowIdsOrError as Array<number>;
        return commonControllers.bases.deleteRowsByIds(baseName.value, tableName.value, rowIds);
    }

    @Function({
        title: 'TABLE_DELETE_ROWS(baseName, tableName, queryFrame)',
        tip: 'Delete table rows, that match the query',
        alias: 'TABLE_DELETE_ROWS',
        lazyOnly: true,
        example: 'TABLE_DELETE_ROWS("main_base", "users", A1:C5)',
        icon: '',
        returns: {
            type: Type.Boolean,
            title: 'Delete result',
            tip: 'Delete result'
        }
    })
    @bind
    public async tableDeleteRows(
        @Param({
            title: 'baseName',
            tip: 'Base name',
            type: Type.String
        }) baseName:AString,
        @Param({
            title: 'tableName',
            tip: 'Table name',
            type: Type.String
        }) tableName:AString,
        @Param({
            title: 'queryFrame',
            tip: 'Query frame',
            type: Type.Frame
        }) queryFrame:IFrame):Promise<ABoolean | AError>
    {
        try
        {
            const {query, settings} = new QueryParser(queryFrame).parse();
            return await commonControllers.bases.deleteRowsByQuery(baseName.value, tableName.value, query);
        }
        catch( e )
        {
            return new AError(e.message);
        }
    }

    @Function({
        title: 'DELETE_RECORDS(baseName, tableName, queryFrame)',
        tip: 'Delete table rows, that match the query',
        alias: 'DELETE_RECORDS',
        lazyOnly: true,
        example: 'DELETE_RECORDS("main_base", "users", A1:C5)',
        icon: '',
        returns: {
            type: Type.Boolean,
            title: 'Delete result',
            tip: 'Delete result'
        }
    })
    @bind
    public async deleteRecords(
        @Param({
            title: 'baseName',
            tip: 'Base name',
            type: Type.String
        }) baseName:AString,
        @Param({
            title: 'tableName',
            tip: 'Table name',
            type: Type.String
        }) tableName:AString,
        @Param({
            title: 'queryFrame',
            tip: 'Query frame',
            type: Type.Frame
        }) queryFrame:IFrame):Promise<ABoolean | AError>
    {
        try
        {
            const {query, settings} = new QueryParser(queryFrame).parse();
            return await commonControllers.bases.deleteRowsByQuery(baseName.value, tableName.value, query);
        }
        catch( e )
        {
            return new AError(e.message);
        }
    }

    @Function({
        title: 'TABLE_UPDATE_ROWS_BY_ID(baseName, tableName, frameWithIdColumn)',
        tip: 'Update existing rows in table',
        alias: 'TABLE_UPDATE_ROWS_BY_ID',
        lazyOnly: true,
        example: 'TABLE_UPDATE_ROWS_BY_ID("projects_base", "task", frameWithIdColumn)',
        icon: '',
        returns: {
            type: Type.Boolean,
            title: 'Update result',
            tip: 'Update result'
        }
    })
    @bind
    public async tableUpdateRowsById(
        @Param({
            title: 'baseName',
            tip: 'Base name',
            type: Type.String
        }) baseName:AString,
        @Param({
            title: 'tableName',
            tip: 'Table name',
            type: Type.String
        }) tableName:AString,
        @Param({
            title: 'frameWithIdColumn',
            tip: 'Frame with _id column',
            type: Type.Frame
        }) frameWithIdColumn:IFrame):Promise<ABoolean | AError>
    {
        return commonControllers.bases.updateRowsByIds(baseName.value, tableName.value, frameWithIdColumn);
    }

    @Function({
        title: 'UPDATE_RECORDS_BY_ID(baseName, tableName, frameWithIdColumn)',
        tip: 'Update existing records in table',
        alias: 'UPDATE_RECORDS_BY_ID',
        lazyOnly: true,
        example: 'UPDATE_RECORDS_BY_ID("projects_base", "task", frameWithIdColumn)',
        icon: '',
        returns: {
            type: Type.Boolean,
            title: 'Update result',
            tip: 'Update result'
        }
    })
    @bind
    public async tableUpdateRecordsById(
        @Param({
            title: 'baseName',
            tip: 'Base name',
            type: Type.String
        }) baseName:AString,
        @Param({
            title: 'tableName',
            tip: 'Table name',
            type: Type.String
        }) tableName:AString,
        @Param({
            title: 'frameWithIdColumn',
            tip: 'Frame with _id column',
            type: Type.Frame
        }) frameWithIdColumn:IFrame):Promise<ABoolean | AError>
    {
        return commonControllers.bases.updateRowsByIds(baseName.value, tableName.value, frameWithIdColumn);
    }

    @Function({
        title: 'TABLE_UPDATE_ROWS(baseName, tableName, queryFrame, frameWithSingleRow)',
        tip: 'Update tables rows, that match the query',
        alias: 'TABLE_UPDATE_ROWS',
        lazyOnly: true,
        example: 'TABLE_UPDATE_ROWS("main_base", "users", A1:C5, B5)',
        icon: '',
        returns: {
            type: Type.Boolean,
            title: 'Update result',
            tip: 'Update result'
        }
    })
    @bind
    public async tableUpdateRows(
        @Param({
            title: 'baseName',
            tip: 'Base name',
            type: Type.String
        }) baseName:AString,
        @Param({
            title: 'tableName',
            tip: 'Table name',
            type: Type.String
        }) tableName:AString,
        @Param({
            title: 'queryFrame',
            tip: 'Query frame',
            type: Type.Frame
        }) queryFrame:IFrame,
        @Param({
            title: 'updateFrame',
            tip: 'Frame with single row to get udpated values from',
            type: Type.Frame
        }) updateFrame:IFrame):Promise<ABoolean | AError>
    {
        try
        {
            const {query, settings} = new QueryParser(queryFrame).parse();
            return await commonControllers.bases.updateRowsByQuery(baseName.value, tableName.value, query, updateFrame);
        }
        catch( e )
        {
            return new AError(e.message);
        }
    }

    @Function({
        title: 'UPDATE_RECORDS(baseName, tableName, queryFrame, frameWithSingleRow)',
        tip: 'Update tables records, that match the query',
        alias: 'UPDATE_RECORDS',
        lazyOnly: true,
        example: 'UPDATE_RECORDS("main_base", "users", A1:C5, B5)',
        icon: '',
        returns: {
            type: Type.Boolean,
            title: 'Update result',
            tip: 'Update result'
        }
    })
    @bind
    public async tableUpdateRecords(
        @Param({
            title: 'baseName',
            tip: 'Base name',
            type: Type.String
        }) baseName:AString,
        @Param({
            title: 'tableName',
            tip: 'Table name',
            type: Type.String
        }) tableName:AString,
        @Param({
            title: 'queryFrame',
            tip: 'Query frame',
            type: Type.Frame
        }) queryFrame:IFrame,
        @Param({
            title: 'updateFrame',
            tip: 'Frame with single row to get udpated values from',
            type: Type.Frame
        }) updateFrame:IFrame):Promise<ABoolean | AError>
    {
        try
        {
            const {query, settings} = new QueryParser(queryFrame).parse();
            return await commonControllers.bases.updateRowsByQuery(baseName.value, tableName.value, query, updateFrame);
        }
        catch( e )
        {
            return new AError(e.message);
        }
    }

    private _parseFrameToSpec(specFrame:IFrame):TableSpec | AError
    {
        const data = specFrame.getDataUnsafe();
        const columnSpecs = [];
        for( const row of data )
        {
            const nameValue = CoercionUtils.coerce(row[0], Type.String, null);
            if( Type.isError(nameValue) )
                return nameValue as AError;

            if( Type.isInstanceOfIValue(nameValue) && !TableFunctions.NAME_REGEXP.test((nameValue as AString).value) )
                return new AError(`#INVALID_COLUMN_NAME: ${nameValue}`);

            const typeValue = CoercionUtils.coerce(row[1], Type.String, null);
            if( Type.isError(typeValue) )
                return typeValue as AError;

            const uniqueValue = CoercionUtils.coerce(row[2], Type.Boolean, null);
            if( Type.isError(uniqueValue) )
                return uniqueValue as AError;

            columnSpecs.push(new ColumnSpec(
                (nameValue as AString).value,
                (typeValue as AString).value,
                (uniqueValue as ABoolean).value,
                false
            ));
        }
        return new TableSpec(columnSpecs, null, null, null);
    }
}
